import { NavLink } from 'react-router-dom';
import {
    AnalyticsUp,
    ClipboardText,
    Home3,
    Lightning,
    Package,
    ShippingTruck,
    ShoppingCart,
    Storefront,
    User,
} from 'shared/icons';
import { useAuth } from '../../hooks/useAuth';
import useIconColor from '../../hooks/useIconColor';

// Página Inicial é a home — item solo, fora de qualquer grupo (espelha o
// Sidebar.jsx desktop: não é "Vendas" nem nenhuma categoria).
const HOME_ITEM = {
    path: '/',
    label: 'Início',
    Icon: Home3,
    permission: null,
    devices: ['desktop', 'mobile'],
};

// Espelha o agrupamento do Sidebar.jsx (Vendas → Compras & Logística → Administração;
// Perfil por último, como o rodapé do desktop). A barra inferior não tem headers,
// então os grupos definem a ORDEM dos itens — manter paridade com o desktop.
// `desktopHint` marca telas densas (workbench multi-painel): mostra o badge "desktop"
// como sub-rótulo do item — sinaliza sem bloquear a navegação.
const MENU_GROUPS = [
    {
        label: 'Vendas',
        items: [
            {
                path: '/operacoes',
                label: 'Operações',
                Icon: AnalyticsUp,
                permission: 'stock:read',
                devices: ['desktop', 'mobile'],
                desktopHint: true,
            },
            {
                path: '/orders',
                label: 'Pedidos',
                Icon: Package,
                permission: 'orders:read',
                devices: ['desktop', 'mobile'],
            },
            {
                // Gestão de Pedidos (2026-09-22) — tabela densa: badge "desktop".
                path: '/pedidos',
                label: 'Gestão',
                Icon: ShoppingCart,
                permission: 'orders:read',
                devices: ['desktop', 'mobile'],
                desktopHint: true,
            },
        ],
    },
    {
        label: 'Compras & Logística',
        items: [
            {
                path: '/wms',
                label: 'WMS',
                Icon: ShippingTruck,
                permission: 'wms:read',
                devices: ['desktop', 'mobile'],
            },
        ],
    },
    {
        // Módulo WMS novo (M0/M6). Só as telas DE CHÃO entram no mobile — são as
        // feitas pro navegador do coletor; os consoles (Configurações, Fábrica)
        // são desktop do Supervisor.
        label: 'Armazém',
        items: [
            {
                path: '/warehouse/collector',
                label: 'Coletor',
                Icon: ClipboardText,
                permission: 'wms:operate',
                devices: ['desktop', 'mobile'],
            },
            {
                // M6 — a bancada da máquina. Mesma permissão do coletor: quem
                // opera passa o turno nesta tela, e a conta do chão vê poucos
                // itens na barra (a filtragem por permissão já enxuga).
                path: '/warehouse/bench',
                label: 'Bancada',
                Icon: Lightning,
                permission: 'wms:operate',
                devices: ['desktop', 'mobile'],
            },
        ],
    },
    {
        label: 'Administração',
        items: [
            {
                path: '/organizations',
                label: 'Orgs',
                Icon: Storefront,
                permission: 'organizations:read',
                devices: ['desktop', 'mobile'],
            },
        ],
    },
    {
        label: 'Conta',
        items: [
            {
                path: '/profile',
                label: 'Perfil',
                Icon: User,
                permission: null,
                devices: ['desktop', 'mobile'],
            },
        ],
    },
];

const MobileSidebar = () => {
    const { checkPermission } = useAuth();
    const iconColor = useIconColor();

    const hasPermission = (permission) => {
        return checkPermission(permission);
    };

    // Home primeiro, depois os grupos achatados preservando a ordem; filtra
    // por permissao e dispositivo (apenas mobile).
    const filteredItems = [HOME_ITEM, ...MENU_GROUPS.flatMap((group) => group.items)].filter((item) => {
        if (!item.devices.includes('mobile')) {
            return false;
        }
        return hasPermission(item.permission);
    });

    return (
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-white dark:bg-mono-950 border-t border-mono-200 dark:border-mono-800 flex items-center justify-around z-40 px-1">
            {filteredItems.map((item) => {
                const Icon = item.Icon;
                return (
                    <NavLink
                        key={item.path}
                        to={item.path}
                        aria-label={item.desktopHint ? `${item.label} (otimizada para desktop)` : item.label}
                        className={({ isActive }) =>
                            `flex flex-col items-center justify-center w-full h-full transition-colors ${
                                isActive ? 'text-black dark:text-white' : 'text-mono-500 dark:text-mono-500'
                            }`
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <Icon
                                    size={28}
                                    weight={isActive ? 'fill' : 'regular'}
                                    style={{ color: isActive ? iconColor : undefined }}
                                />
                                {item.desktopHint && (
                                    <span className="mt-0.5 rounded bg-mono-100 dark:bg-mono-900 px-1 text-[8px] font-semibold uppercase leading-none tracking-wide text-mono-500">
                                        desktop
                                    </span>
                                )}
                            </>
                        )}
                    </NavLink>
                );
            })}
        </nav>
    );
};

export default MobileSidebar;
